"use strict";
/**
 * Idempotently provision streams under `:_cmc:apps` on a CMC-enabled account.
 *
 * The CMC plugin owns the `:_cmc:apps` namespace. The per-app leaf
 * (`:_cmc:apps:<appCode>`) is auto-provisioned server-side by the
 * `cmcAccessProvisionAppScopeHook` post-hook on `accesses.create` /
 * `accesses.update`, whenever the access references a matching
 * `:_cmc:apps:<appCode>:*` permission (deployed 2026-05-26, plan-61
 * upstream fix). Callers therefore do not need to `streams.create` the
 * leaf — this helper returns its id directly.
 *
 * Sub-scopes (`:_cmc:apps:<appCode>:<subPath>`) are NOT auto-provisioned
 * and still require a `streams.create` here.
 *
 * Hoisted from three independent copies that had drifted across the
 * doctor-dashboard, the Mira bridge, and an archived migration script.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.pryvErrorCode = pryvErrorCode;
exports.ensureAppScope = ensureAppScope;
const patchedPryv_ts_1 = require("../patchedPryv.js");
/** Extract a Pryv API error-code id from a thrown error, regardless of nesting depth. */
function pryvErrorCode(e) {
    const inner = e?.innerObject?.id;
    if (inner != null)
        return inner;
    return e?.id;
}
/**
 * Resolve `:_cmc:apps:<appCode>` (and optionally provision
 * `:_cmc:apps:<appCode>:<subPath>`) on the caller's account.
 *
 * @param connection  caller's Pryv connection (doctor / patient / bridge).
 * @param appCode     `hds-collector`, `hds-patient`, `hds-bridge-mira`, etc.
 * @param subPath     optional leaf scope under the appScope (e.g. a collectorId).
 * @returns           the streamId of the deepest scope.
 */
async function ensureAppScope(connection, appCode, subPath) {
    const appScope = patchedPryv_ts_1.cmc.appScope(appCode); // :_cmc:apps:<appCode>
    if (subPath == null)
        return appScope;
    const sub = appScope + ':' + subPath;
    try {
        await connection.apiOne('streams.create', { id: sub, parentId: appScope, name: subPath });
    }
    catch (e) {
        const code = pryvErrorCode(e);
        if (code !== 'item-already-exists')
            throw e;
    }
    return sub;
}
